import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { useSectionAccess } from '../../hooks/useSectionAccess';

export const ProductionInnerTabs = ({ tabs = [], active, onChange }) => {
  const ui = useUIColors();
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        padding: '0 12px',
        borderBottom: `1px solid ${ui.border}`,
        background: ui.header,
        overflowX: 'auto',
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            style={{
              appearance: 'none',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '10px 12px',
              marginBottom: -1,
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              color: isActive ? ui.text : ui.muted,
              borderBottom: isActive ? '2px solid #16a34a' : '2px solid transparent',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {tab.label}
            {tab.count != null ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#16a34a' : ui.muted }}>
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Tabla de producción al estilo Table3: búsqueda, filtros opcionales y selección múltiple.
 * filters: [{ key, label, getValue?: (row) => string }]
 *   Las opciones se derivan de los valores presentes en rows.
 */
const ProductionDataTable = ({
  title,
  subtitle,
  rows = [],
  columns = [],
  renderCells,
  onRowClick,
  searchPlaceholder = 'Buscar…',
  defaultSortKey = '',
  defaultSortDir = 'asc',
  action = null,
  tabs = null,
  activeTab = null,
  onTabChange = null,
  footer = null,
  filters = null,
  /** Contenido adicional en la barra de búsqueda (p. ej. un select al costado). */
  toolbarExtra = null,
  selectable = false,
  selectedIds = null,
  onSelectionChange = null,
  getRowId = (row) => row.id || row.code,
  bulkActions = null,
  /** Color de la barra junto al checkbox (p. ej. según estado). */
  getRowAccent = null,
}) => {
  const ui = useUIColors();
  const access = useSectionAccess();
  const visibleAction = access.canCreate ? action : null;
  const visibleBulkActions = access.canDelete ? bulkActions : null;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(defaultSortKey || columns[0]?.sortKey || '');
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [filterValues, setFilterValues] = useState({});
  const [internalSelected, setInternalSelected] = useState(() => new Set());

  const controlledSelection = selectedIds != null && typeof onSelectionChange === 'function';
  const selectedSet = useMemo(() => {
    if (controlledSelection) return new Set(selectedIds || []);
    return internalSelected;
  }, [controlledSelection, selectedIds, internalSelected]);

  const setSelectedSet = (next) => {
    if (controlledSelection) onSelectionChange([...next]);
    else setInternalSelected(next);
  };

  useEffect(() => {
    if (!selectable) return;
    const valid = new Set(rows.map((row) => String(getRowId(row))));
    const prune = (prev) => {
      const next = new Set([...prev].filter((id) => valid.has(String(id))));
      return next.size === prev.size ? prev : next;
    };
    if (controlledSelection) {
      const next = [...selectedSet].filter((id) => valid.has(String(id)));
      if (next.length !== selectedSet.size) onSelectionChange(next);
      return;
    }
    setInternalSelected(prune);
  }, [rows, selectable]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterDefs = useMemo(() => (Array.isArray(filters) ? filters : []), [filters]);

  const filterOptions = useMemo(() => {
    const map = {};
    filterDefs.forEach((def) => {
      const values = new Set();
      rows.forEach((row) => {
        if (typeof def.getOptions === 'function') {
          (def.getOptions(row) || []).forEach((item) => {
            const value = String(item || '').trim();
            if (value) values.add(value);
          });
          return;
        }
        const raw = def.getValue ? def.getValue(row) : row[def.key];
        const value = String(raw || '').trim();
        if (value) values.add(value);
      });
      map[def.key] = [...values].sort((a, b) => a.localeCompare(b, 'es'));
    });
    return map;
  }, [rows, filterDefs]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    filterDefs.some((def) => String(filterValues[def.key] || '').trim());

  const clearFilters = () => {
    setSearchQuery('');
    setFilterValues({});
  };

  const filtered = useMemo(() => {
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((row) => {
      for (const def of filterDefs) {
        const selected = String(filterValues[def.key] || '').trim();
        if (!selected) continue;
        if (typeof def.matches === 'function') {
          if (!def.matches(row, selected)) return false;
          continue;
        }
        const raw = def.getValue ? def.getValue(row) : row[def.key];
        if (String(raw || '').trim() !== selected) return false;
      }
      if (!tokens.length) return true;
      const hay = Object.values(row)
        .filter((value) => value != null && typeof value !== 'object')
        .join(' ')
        .toLowerCase();
      return tokens.every((token) => hay.includes(token));
    });
  }, [rows, searchQuery, filterDefs, filterValues]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const mult = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = a[sortColumn];
      const vb = b[sortColumn];
      if (String(sortColumn).includes('date') || String(sortColumn).endsWith('_at')) {
        return ((va ? new Date(va).getTime() : 0) - (vb ? new Date(vb).getTime() : 0)) * mult;
      }
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
      return String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true }) * mult;
    });
    return list;
  }, [filtered, sortColumn, sortDir]);

  const visibleIds = useMemo(() => sorted.map((row) => String(getRowId(row))), [sorted, getRowId]);
  const allSelected = selectable && visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const someSelected = selectable && visibleIds.some((id) => selectedSet.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedSet(new Set());
      return;
    }
    setSelectedSet(new Set(visibleIds));
  };

  const toggleOne = (id, event) => {
    event?.stopPropagation?.();
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedSet(next);
  };

  const handleSort = (key) => {
    if (!key) return;
    if (sortColumn === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(key);
    setSortDir(String(key).includes('date') || String(key).includes('qty') ? 'desc' : 'asc');
  };

  const SortIcon = ({ columnKey }) => {
    if (!columnKey) return null;
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={ui.muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={ui.text} /> : <ArrowDown size={12} color={ui.text} />;
  };

  const selectStyle = {
    height: 36,
    minWidth: 140,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.text,
    fontSize: 13,
    outline: 'none',
  };

  const colSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: ui.page,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>{title}</h3>
          <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
            {subtitle || `${sorted.length} de ${rows.length} registros`}
          </p>
        </div>
        {visibleAction}
      </div>

      {tabs ? <ProductionInnerTabs tabs={tabs} active={activeTab} onChange={onTabChange} /> : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
          padding: '10px 16px',
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
            <Search
              size={16}
              color={ui.muted}
              style={{ position: 'absolute', left: 12, pointerEvents: 'none' }}
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              style={{
                height: 36,
                padding: '0 10px 0 36px',
                borderRadius: 8,
                border: `1px solid ${ui.inputBorder}`,
                background: ui.inputBg,
                color: ui.text,
                fontSize: 13,
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </span>
        </label>

        {toolbarExtra}

        {filterDefs.map((def) => (
          <label
            key={def.key}
            style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: ui.muted }}
          >
            {def.label}
            <select
              value={filterValues[def.key] || ''}
              onChange={(event) =>
                setFilterValues((prev) => ({ ...prev, [def.key]: event.target.value }))
              }
              style={selectStyle}
              aria-label={`Filtrar por ${def.label}`}
            >
              <option value="">Todos</option>
              {(filterOptions[def.key] || []).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        ))}

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${ui.border}`,
              background: 'transparent',
              color: ui.muted,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {selectable && selectedSet.size > 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            fontSize: 12,
            color: ui.text,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {selectedSet.size} seleccionada{selectedSet.size === 1 ? '' : 's'}
          </span>
          {visibleBulkActions}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
          <thead>
            <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
              {selectable ? (
                <th style={{ padding: getRowAccent ? '8px 10px 8px 11px' : '8px 10px', width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = !allSelected && someSelected;
                    }}
                    onChange={toggleAll}
                    aria-label="Seleccionar todas"
                  />
                </th>
              ) : null}
              {columns.map((col) => (
                <th
                  key={col.key || col.sortKey}
                  onClick={() => handleSort(col.sortKey)}
                  style={{
                    padding: '8px 10px',
                    textAlign: col.align || 'left',
                    fontSize: 11,
                    fontWeight: 700,
                    color: ui.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    cursor: col.sortKey ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: 24, textAlign: 'center', color: ui.muted }}>
                  Sin registros para mostrar.
                </td>
              </tr>
            ) : (
              sorted.map((row) => {
                const rowId = String(getRowId(row));
                const isSelected = selectable && selectedSet.has(rowId);
                return (
                  <tr
                    key={rowId}
                    style={{
                      borderBottom: `1px solid ${ui.border}`,
                      cursor: onRowClick ? 'pointer' : 'default',
                      background: isSelected ? 'color-mix(in srgb, #16a34a 8%, transparent)' : 'transparent',
                    }}
                    onClick={() => onRowClick?.(row)}
                    onMouseEnter={(event) => {
                      if (!isSelected) event.currentTarget.style.background = ui.rowHover;
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = isSelected
                        ? 'color-mix(in srgb, #16a34a 8%, transparent)'
                        : 'transparent';
                    }}
                  >
                    {selectable ? (
                      <td
                        style={{ padding: getRowAccent ? '6px 10px 6px 0' : '8px 10px' }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {getRowAccent ? (
                          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, minHeight: 36 }}>
                            <div
                              style={{
                                width: 3,
                                flexShrink: 0,
                                background: getRowAccent(row) || 'transparent',
                                borderRadius: '0 2px 2px 0',
                              }}
                            />
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(event) => toggleOne(rowId, event)}
                                aria-label={`Seleccionar ${row.code || rowId}`}
                              />
                            </span>
                          </div>
                        ) : (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(event) => toggleOne(rowId, event)}
                            aria-label={`Seleccionar ${row.code || rowId}`}
                          />
                        )}
                      </td>
                    ) : null}
                    {renderCells(row, ui)}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
};

export default ProductionDataTable;
